<template>
  <div>
    <div class="d-flex justify-content-between">
      <h1 class="page-header">
        {{ $t('school.payment.setup.feeSetup.pageTitle') }}
      </h1>
      <ts-breadcrumb
        :items="[
          { text: $t('toHomePage'), href: '/' },
          {
            text: $t('school.payment.setup.feeSetup.pageTitle'),
            active: true
          }
        ]"
      />
    </div>
    <ts-card>
      <ts-card-wrapper>
        <div class="d-flex justify-content-between">
          <div>
            <ts-button
              color="info"
              outline
              @click="$router.push({ name: 'fee-setup-create' })"
              >{{ $t('add_new') }}</ts-button
            >
            <ts-button class="ml-2" @click.prevent="showFormCopy">{{
              $t('school.payment.setup.feeSetup.copyFee')
            }}</ts-button>
            <ts-button
              outline
              color="success"
              class="ml-2"
              @click.prevent="exportExcel"
              :waiting="exporting"
            >
              <i class="fas fa-file-excel"></i>
              {{ $t('export') }}</ts-button
            >
          </div>
          <div class="d-flex">
            <ts-input
              v-model="search"
              :placeholder="$t('search')"
              style="width: 250px"
              class="inline-block"
            />
            <ts-button
              outline
              class="ml-2"
              v-tooltip="$t('filter')"
              @click.prevent="visible = true"
            >
              <i class="fa fa-filter"></i>
            </ts-button>
          </div>
        </div>
      </ts-card-wrapper>
      <ts-table
        :columns="columns"
        :records="resources"
        :loading="loading"
        :rowGroup="{
          field: 'fee_type.fee_type_name'
        }"
      >
        <template v-slot="{ record }">
          <td>
            {{ record.branch ? record.branch.branch_name_en : '' }}
          </td>
          <td class="kh-font">{{ record.fee_name_en }}</td>
          <td class="kh-font text-center">{{ record.term_semester_order }}</td>
          <td class="kh-font text-center">{{ record.fee_amount }}</td>
          <td class="kh-font text-center">
            {{ record.grade ? record.grade.grade_name_foreign : '' }}
          </td>
          <td class="kh-font text-center">
            {{ record.payment_term ? record.payment_term.payment_term : '' }}
          </td>
          <td class="kh-font text-center">
            {{ record.shift ? record.shift.shift_en : '' }}
          </td>
          <td class="kh-font text-center">
            {{ record.distance ? record.distance.distance_description : '' }}
          </td>
          <td class="kh-font text-center">
            {{ record.way ? record.way.way_description : '' }}
          </td>
          <td>
            {{ record.user ? record.user.loginname : '' }}
          </td>
          <td>
            {{ record.created_date }}
          </td>
          <td class="kh-font text-center">
            <a
              href="#"
              title="Edit"
              class="mr-2 text-primary"
              @click.prevent="edit(record)"
            >
              <i class="fas fa-edit"></i>
            </a>
            <a
              href="#"
              title="Delete"
              class="text-danger"
              @click.prevent="destroy(record)"
              :disabled="record._deleting"
            >
              <i class="fas fa-circle-notch spin" v-if="record._deleting"></i>
              <i class="fas fa-trash" v-else></i>
            </a>
          </td>
        </template>
      </ts-table>
      <div class="d-flex justify-content-end p-15">
        <ts-pagination
          v-model="pagination"
          @navigate="fetchData"
        ></ts-pagination>
      </div>
      <a-drawer
        :title="$t('invoice.filter')"
        placement="right"
        :closable="false"
        :visible="visible"
        @close="onCloseDrawer"
        :zIndex="1020"
        width="300px"
      >
        <div class="row">
          <div class="col-md-12">
            <div class="form-group">
              <a-select
                style="width: 257px"
                v-model="branch_id"
                :options="branchs"
                showSearch
                mode="multiple"
                :filterOption="
                  (input, option) =>
                    option.componentOptions.children[0].text
                      .toLowerCase()
                      .indexOf(input.toLowerCase()) >= 0
                "
                :maxTagCount="1"
                :maxTagTextLength="17"
                :placeholder="$t('all')"
              >
              </a-select>
            </div>
            <div class="form-group">
              <label>{{ $t('school.payment.setup.feeSetup.feeType') }}</label>
              <a-select
                style="width: 257px"
                v-model="fee_type_id"
                :options="getFeeType"
                showSearch
                mode="multiple"
                :filterOption="
                  (input, option) =>
                    option.componentOptions.children[0].text
                      .toLowerCase()
                      .indexOf(input.toLowerCase()) >= 0
                "
                :maxTagCount="1"
                :maxTagTextLength="17"
                :placeholder="$t('all')"
              >
              </a-select>
            </div>
          </div>
        </div>
      </a-drawer>
    </ts-card>
  </div>
</template>

<script>
import { mapState } from 'vuex'
import { debounce, trim } from 'lodash'
import FormComponent from './copy-fee'
import * as FileDownload from 'downloadjs'

export default {
  name: 'feeSetup',
  data () {
    return {
      loading: false,
      branchs: [],
      visible: false,
      exporting: false
    }
  },
  created () {
    this.branch_id = [this.$store.state.authUser.default_branch_id]
    this.fetchData()
    this.getBranch()
  },
  computed: {
    ...mapState('feeSetup', ['resources', 'pagination', 'feeTypes']),
    search: {
      get () {
        return this.$store.state.feeSetup.search
      },
      set (newValue) {
        this.$store.commit('feeSetup/SET_SEARCH', newValue)
        this.$store.commit('feeSetup/RESET_CURRENT_PAGE')
      }
    },
    getFeeType () {
      return this.feeTypes.map(el => ({
        value: el.value,
        label: el.text
      }))
    },
    branch_id: {
      get () {
        return this.$store.state.feeSetup.branch_id
      },
      set (newValue) {
        this.$store.commit('feeSetup/SET_BRANCH_ID', newValue)
      }
    },
    fee_type_id: {
      get () {
        return this.$store.state.feeSetup.fee_type_id
      },
      set (newValue) {
        this.$store.commit('feeSetup/SET_FEE_TYPE_ID', newValue)
      }
    },
    columns () {
      return [
        {
          name: this.$t('school.payment.setup.feeSetup.branch')
        },
        {
          name: this.$t('school.payment.setup.feeSetup.feeName')
        },
        {
          name: this.$t('school.payment.setup.feeSetup.termSemesterOrder'),
          style: 'text-align:center'
        },
        {
          name: this.$t('school.payment.setup.feeSetup.amount'),
          style: 'text-align:center'
        },
        {
          name: this.$t('school.payment.setup.feeSetup.grade'),
          style: 'text-align:center'
        },
        {
          name: this.$t('school.payment.setup.feeSetup.paymentTerm'),
          style: 'text-align:center'
        },
        {
          name: this.$t('school.payment.setup.feeSetup.shift'),
          style: 'text-align:center'
        },
        {
          name: this.$t('school.payment.setup.feeSetup.distance'),
          style: 'text-align:center'
        },
        {
          name: this.$t('school.payment.setup.feeSetup.transaction'),
          style: 'text-align:center'
        },
        {
          name: this.$t('school.payment.setup.feeSetup.createdBy')
        },
        {
          name: this.$t('school.payment.setup.feeSetup.createdDate')
        },
        {
          name: this.$t('actions'),
          style: 'text-align:center; width:5%'
        }
      ]
    }
  },
  methods: {
    fetchData (attributes = {}) {
      this.loading = true
      this.$store
        .dispatch(
          'feeSetup/fetch',
          Object.assign(
            {
              fee_type_id: this.fee_type_id,
              branch_id: this.branch_id
            },
            { ...attributes }
          )
        )
        .then(() => {
          this.loading = false
        })
        .catch(error => {
          this.loading = false
          this.$toasted.error(error.message)
        })
    },
    getBranch () {
      this.$store
        .dispatch('user/getBranch', {
          filter: {
            user_branches: true
          }
        })
        .then(response => {
          this.branchs = response.data.map(el => ({
            value: el.branch_id,
            label: el.branch_name_en
          }))
        })
    },
    edit (record) {
      this.$store.commit('feeSetup/SET_EDIT_DATA', record)
      this.$router.push({
        name: 'fee-setup-edit',
        params: { id: record.fee_setup_id }
      })
    },
    async destroy (record) {
      if (await this.$confirmModal.danger(this.$t('are_you_sure_to_delete'))) {
        record._deleting = true
        this.$store
          .dispatch('feeSetup/destroy', record.fee_setup_id)
          .then(response => {
            this.$toasted.success(response.message)
            this.fetchData()
          })
          .catch(error => {
            this.$toasted.error(error.message)
          })
      }
    },
    showFormCopy () {
      this.$modal.show(
        FormComponent,
        {},
        {
          height: 'auto',
          width: '20%',
          scrollable: true,
          clickToClose: false
        }
      )
    },
    onCloseDrawer () {
      this.visible = false
    },
    exportExcel () {
      this.exporting = true

      this.$store
        .dispatch('feeSetup/exportExcel')
        .then(response => {
          let filename = trim(
            response.headers['content-disposition']
              .substring(
                response.headers['content-disposition'].indexOf('filename')
              )
              .replace('filename=', ''),
            '"'
          )
          FileDownload(response.data, filename)
        })
        .catch(err => {
          let error = JSON.parse(
            String.fromCharCode.apply(null, new Uint8Array(err))
          )
          this.$toasted.error(error.message)
        })
        .finally(() => (this.exporting = false))
    }
  },
  watch: {
    search: debounce(function (search) {
      this.fetchData({ search })
    }, 500),
    branch_id: debounce(function (value) {
      this.fetchData({ branch_id: value })
    }, 500),
    fee_type_id: debounce(function () {
      this.fetchData()
    }, 500)
  },
  beforeRouteLeave (to, from, next) {
    if (_.isEmpty(to.params) && to.name != 'fee-setup-create') {
      this.$store.commit('feeSetup/RESET_STATE')
    }
    next()
  }
}
</script>
